/**
 * A tidy layered layout for a graph that is really a tree.
 *
 * What this replaces was a spring simulation: every pair of nodes repelling,
 * every edge a spring, two iterations per animation frame, forever. That is
 * the right tool for a graph with no inherent direction and the wrong one for
 * this graph, which is a hierarchy - a bucket, its tools, and the records and
 * sealed objects under each tool. Force layouts discard edge direction by
 * construction, so a hierarchy comes out as radial spaghetti; the literature
 * calls the result a hairball and it is exactly what was on screen.
 *
 * The fix is not better constants. It is the Reingold-Tilford reading: depth
 * decides the column, and sibling order decides the row. Leaves take
 * consecutive rows and every parent sits at the midpoint of its children, so
 * no two nodes overlap, no tree edge crosses another, and the same model
 * always produces the same picture. It is computed once when the model
 * changes rather than continuously, which is why the result can be still.
 *
 * The graph is not a pure tree: the record projection carries record-to-record
 * edges that are real and are not parentage. Those are separated out here
 * rather than fed to the layout, so the tree stays legible and they can be
 * drawn behind it as arcs, which is what they are - a second relation over the
 * same nodes.
 */

/** Where one node landed. `depth` is its column, `row` its slot in the breadth axis. */
export interface Placement {
  x: number;
  y: number;
  depth: number;
  row: number;
}

export interface TidyLayout {
  at: Map<string, Placement>;
  /** Edges that are parentage, in the spanning forest. Drawn as the tree. */
  treeEdges: [string, string][];
  /** Real edges that are not parentage. Drawn behind, as arcs. */
  crossEdges: [string, string][];
  /** How many columns deep the forest runs. */
  depth: number;
  bounds: { minX: number; maxX: number; minY: number; maxY: number };
}

export interface TidyOptions {
  /** Distance between one column and the next, centre to centre. */
  columnGap: number;
  /** Distance between one row and the next, centre to centre. */
  rowGap: number;
  /** Blank rows inserted between two roots of the forest. */
  rootGap: number;
  /**
   * Rows a band may hold before the next root starts a new band to the right.
   *
   * A forest of unconnected roots is not a tree and stacking it in one column
   * is the shape that broke the domain view: 36 domains with no parent between
   * them became a column 1,440 units tall in a viewport 450 high, so fitting
   * it meant 15% and every label was a smudge. Banding turns a flat set into a
   * grid, which is what a set wants to be.
   *
   * Omit it, or pass 0, to keep one band however tall it grows.
   */
  maxRows?: number;
}

interface Node {
  id: string;
  children: string[];
  row: number;
  depth: number;
}

/** Leaves under a root, which is exactly the rows its subtree will occupy. */
function subtreeLeaves(root: string, placed: Map<string, Node>): number {
  let leaves = 0;
  const stack = [root];
  while (stack.length > 0) {
    const node = placed.get(stack.pop()!)!;
    if (node.children.length === 0) leaves += 1;
    else for (const child of node.children) stack.push(child);
  }
  return leaves;
}

/**
 * How many columns to the right of its root a subtree reaches.
 *
 * Counts the wrap columns of a fanned-out leaf set too, or the next band would
 * be placed on top of the widest thing in this one.
 */
function subtreeDepth(root: string, placed: Map<string, Node>, extra: Map<string, number>): number {
  const base = placed.get(root)!.depth;
  let deepest = 0;
  const stack = [root];
  while (stack.length > 0) {
    const id = stack.pop()!;
    const node = placed.get(id)!;
    deepest = Math.max(deepest, node.depth - base + (extra.get(id) ?? 0));
    for (const child of node.children) stack.push(child);
  }
  return deepest;
}

/**
 * Build the spanning forest, then place it.
 *
 * Parentage is taken from the FIRST edge that reaches a node, walking roots in
 * their given order and breadth-first. That makes the choice deterministic:
 * the same model yields the same parent for every node on every render, which
 * is what stops the picture rearranging itself while someone is reading it.
 */
export function tidyForest(
  ids: readonly string[],
  edges: readonly (readonly [string, string])[],
  options: TidyOptions,
): TidyLayout {
  const present = new Set(ids);
  const real = edges.filter(([a, b]) => a !== b && present.has(a) && present.has(b));

  const children = new Map<string, string[]>();
  const indegree = new Map<string, number>();
  for (const id of ids) {
    children.set(id, []);
    indegree.set(id, 0);
  }
  for (const [a, b] of real) {
    children.get(a)!.push(b);
    indegree.set(b, (indegree.get(b) ?? 0) + 1);
  }

  // A root is a node nothing points at. A graph made entirely of cycles has
  // none, so the first id stands in rather than the layout returning nothing.
  const roots = ids.filter((id) => (indegree.get(id) ?? 0) === 0);
  const starts = roots.length > 0 ? roots : ids.length > 0 ? [ids[0]!] : [];

  const parent = new Map<string, string>();
  const bandOf = new Map<string, number>();
  const placed = new Map<string, Node>();
  const order: string[] = [];

  for (const root of starts) {
    if (placed.has(root)) continue;
    const queue: [string, number][] = [[root, 0]];
    placed.set(root, { id: root, children: [], row: 0, depth: 0 });
    order.push(root);
    while (queue.length > 0) {
      const [id, depth] = queue.shift()!;
      placed.get(id)!.depth = depth;
      for (const child of children.get(id) ?? []) {
        if (placed.has(child)) continue;
        placed.set(child, { id: child, children: [], row: 0, depth: depth + 1 });
        parent.set(child, id);
        placed.get(id)!.children.push(child);
        order.push(child);
        queue.push([child, depth + 1]);
      }
    }
  }

  // Anything unreachable from a root - a node whose every edge points into a
  // cycle - is still a node someone can see, so it becomes its own root rather
  // than vanishing from a picture that claims to show the whole model.
  for (const id of ids) {
    if (placed.has(id)) continue;
    placed.set(id, { id, children: [], row: 0, depth: 0 });
    order.push(id);
    starts.push(id);
  }

  const treeEdges: [string, string][] = [];
  const crossEdges: [string, string][] = [];
  for (const [a, b] of real) {
    if (parent.get(b) === a) treeEdges.push([a, b]);
    else crossEdges.push([a, b]);
  }

  /**
   * Rows, bottom up.
   *
   * A leaf takes the next free row. A parent takes the midpoint of its first
   * and last child, which is the whole of Reingold-Tilford for nodes of equal
   * breadth: consecutive leaves cannot collide, and a parent centred over its
   * own children cannot stray into a sibling subtree's rows.
   *
   * Iterative rather than recursive because a record chain can run deeper than
   * a comfortable stack, and a layout that throws on a deep graph is worse
   * than one that is merely slow.
   */
  let nextRow = 0;
  let bandDepth = 0;
  let bandWidest = 0;
  /**
   * Extra columns for a leaf set too tall to read as one.
   *
   * A tidy tree puts every child of a node in one column, which is right until
   * a bucket has eighteen tools or three hundred records: the column runs off
   * the screen while the width beside it sits empty. When a node's children
   * are ALL leaves - so no subtree can be split by doing this - they wrap into
   * a grid instead, and the parent centres on the block.
   */
  const extraDepth = new Map<string, number>();
  const uniqueRoots = starts.filter((id, i) => starts.indexOf(id) === i);
  for (const root of uniqueRoots) {
    const node = placed.get(root);
    if (!node) continue;
    // A band is full when the next root cannot fit in it. The check is before
    // the root is placed rather than after, so a subtree is never split across
    // two bands: half a tree in each column is worse than a taller column.
    const height = subtreeLeaves(root, placed);
    if (options.maxRows && nextRow > 0 && nextRow + height > options.maxRows) {
      bandDepth += bandWidest + 1;
      bandWidest = 0;
      nextRow = 0;
    }
    const stack: { id: string; visited: boolean }[] = [{ id: root, visited: false }];
    while (stack.length > 0) {
      const frame = stack.pop()!;
      const current = placed.get(frame.id)!;
      if (!frame.visited) {
        if (current.children.length === 0) {
          current.row = nextRow;
          nextRow += 1;
          continue;
        }
        const rows = options.maxRows ?? 0;
        const allLeaves = current.children.every((c) => placed.get(c)!.children.length === 0);
        if (rows > 0 && allLeaves && current.children.length > rows) {
          current.children.forEach((childId, i) => {
            const child = placed.get(childId)!;
            child.row = nextRow + (i % rows);
            extraDepth.set(childId, Math.floor(i / rows));
          });
          const used = Math.min(current.children.length, rows);
          current.row = nextRow + (used - 1) / 2;
          nextRow += used;
          continue;
        }
        stack.push({ id: frame.id, visited: true });
        // Pushed in reverse so they are popped in their given order, which is
        // the order the model added them.
        for (let i = current.children.length - 1; i >= 0; i--) {
          stack.push({ id: current.children[i]!, visited: false });
        }
      } else {
        const first = placed.get(current.children[0]!)!;
        const last = placed.get(current.children[current.children.length - 1]!)!;
        current.row = (first.row + last.row) / 2;
      }
    }
    // The gap separates one TREE from the next. A root with no children is not
    // a tree, it is a member of a set, and putting a blank row after each of 36
    // domains doubled the height of the view for no reading benefit.
    if (node.children.length > 0) nextRow += options.rootGap;
    bandWidest = Math.max(bandWidest, subtreeDepth(root, placed, extraDepth));
    bandOf.set(root, bandDepth);
  }

  // Every node inherits its root's band, so a subtree travels with its parent.
  const bandFor = new Map<string, number>();
  for (const [root, band] of bandOf) {
    const stack = [root];
    while (stack.length > 0) {
      const id = stack.pop()!;
      bandFor.set(id, band);
      for (const child of placed.get(id)!.children) stack.push(child);
    }
  }

  const at = new Map<string, Placement>();
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let deepest = 0;
  for (const id of order) {
    const node = placed.get(id)!;
    const x = (node.depth + (bandFor.get(id) ?? 0) + (extraDepth.get(id) ?? 0)) * options.columnGap;
    const y = node.row * options.rowGap;
    at.set(id, { x, y, depth: node.depth, row: node.row });
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
    deepest = Math.max(deepest, node.depth);
  }

  return {
    at,
    treeEdges,
    crossEdges,
    depth: deepest,
    bounds:
      order.length > 0
        ? { minX, maxX, minY, maxY }
        : { minX: 0, maxX: 0, minY: 0, maxY: 0 },
  };
}
